/**
 * @typedef {Object} SchedulingZoomLevel
 * @property {'year' | 'month'} type
 * @property {number} size
 * @property {'day' | 'hour'} resolutionHorizontal
 * @property {'small' | 'medium' | 'large' | 'xlarge' | 'xxxlarge'} columnWidth
 * @property {'day'} resolution
 * @property {number} [timeCellStep]
 * @property {number} [timeLabelStep]
 */

/**
 * @typedef {string} SchedulingEventColor
 */

/**
 * @typedef {Object} SchedulingFilterPrefix
 * @property {'client.'} CLIENT
 * @property {'staffMembers.'} STAFF_MEMBER
 * @property {'user.'} USER
 */

/**
 * @typedef {string} AppointmentStatus
 */

/** @type {Record<number, SchedulingZoomLevel>} */
// eslint-disable-next-line import/prefer-default-export
export const SCHEDULING_ZOOM_LEVELS = {
  1: {
    type: 'year',
    size: 1,
    resolutionHorizontal: 'day',
    columnWidth: 'small',
    resolution: 'day',

  },
  2: {
    type: 'year',
    size: 1,
    resolutionHorizontal: 'day',
    columnWidth: 'xlarge',
    resolution: 'day',

  },
  3: {
    type: 'month',
    size: 1,
    resolutionHorizontal: 'hour',
    columnWidth: 'xlarge',
    timeCellStep: 720,
    timeLabelStep: 720,
    resolution: 'day',

  },
  4: {
    type: 'month',
    size: 1,
    resolutionHorizontal: 'hour',
    columnWidth: 'xlarge',
    timeCellStep: 360,
    timeLabelStep: 360,
    resolution: 'day',

  },
  5: {
    type: 'day',
    size: 1,
    resolutionHorizontal: 'hour',
    columnWidth: 'xlarge',
    timeCellStep: 180,
    timeLabelStep: 180,
    resolution: 'day',

  },
  6: {
    type: 'day',
    size: 1,
    resolutionHorizontal: 'hour',
    columnWidth: 'medium',
    timeCellStep: 60,
    timeLabelStep: 60,
    resolution: 'day',

  },
  7: {
    type: 'day',
    size: 1,
    resolutionHorizontal: 'hour',
    timeCellStep: 30,
    timeLabelStep: 30,
    columnWidth: 'medium',
    resolution: 'day',

  },
  8: {
    type: 'day',
    size: 1,
    resolutionHorizontal: 'hour',
    timeCellStep: 30,
    timeLabelStep: 30,
    columnWidth: 'xxxlarge',
    resolution: 'day',

  },
  9: {
    type: 'day',
    size: 1,
    resolutionHorizontal: 'hour',
    timeCellStep: 15,
    timeLabelStep: 15,
    columnWidth: 'xxxlarge',
    resolution: 'day',

  },
  10: {
    type: 'day',
    size: 1,
    resolutionHorizontal: 'hour',
    timeCellStep: 5,
    timeLabelStep: 5,
    columnWidth: 'large',
    resolution: 'day',

  },
};

/** @type {SchedulingEventColor[]} */
export const SCHEDULING_EVENT_COLOR = [
  'blue',
  'red',
  'green',
  'purple',
  'orange',
  'teal',
  'pink',
  'indigo',
  'cyan',
  'lime',
  'amber',
  'deep-orange',
  'deep-purple',
  'light-blue',
  'light-green',
  'yellow',
  'brown',
  'blue-grey',
];

/** @type {SchedulingFilterPrefix} */
export const SCHEDULING_FILTER_PREFIX = {
  CLIENT: 'client.',
  STAFF_MEMBER: 'staffMembers.',
  USER: 'user.',
};

/** @type {AppointmentStatus[]} */
export const SCHEDULING_RECURRENCE_EXCLUDED_STATUSES = ['completed', 'cancelled', 'deleted', 'rendered'];

/** @type {AppointmentStatus[]} */
export const VALID_APPOINTMENT_STATUSES = [
  'created',
  'draft',
  'scheduled',
  'completed',
  'in-progress',
  'rendered',
  'cancelled',
];

/** @type {AppointmentStatus[]} */
export const SCHEDULING_HOURS_TOTAL_EXCLUDED_STATUSES = ['cancelled', 'deleted'];

/**
 * Resource types whose column header shows a scheduled-hours total, and how each is summed. The key
 * doubles as the `resourceType` value on the calendar's resource objects; `resourceKey`/`excludeTypes`
 * are passed straight through to `sumScheduledSecondsByResource`.
 * @type {Record<string, { resourceKey: string, excludeTypes?: string[], excludeNonPayable?: boolean }>}
 */
export const SCHEDULING_HOURS_TOTAL_RESOURCE_TYPES = {
  user: { resourceKey: 'user', excludeTypes: ['unavailability'], excludeNonPayable: true },
  // A staffEvent can carry a client id, but it is the staff member's time, not the client's.
  client: { resourceKey: 'client', excludeTypes: ['unavailability', 'staffEvent'] },
};
